import { useEffect, useState } from 'react';
import { useGetApiV1ThemeActive } from '@/shared/api/generated/theme/theme';
import { DEFAULT_THEME, type ThemeTokens } from '@/shared/config/theme/default-theme';
import { useThemeMode } from '@/shared/config/theme/theme-mode-context';

/** A colour token's name, exactly as the theme document spells it. */
export type ColourToken = keyof ThemeTokens['colors'];

/**
 * The resolved value of theme colour tokens, for the consumers that cannot use a CSS variable.
 *
 * ⚠️ **MapLibre paints on a canvas and Apex writes inline SVG attributes; neither knows anything
 * about the cascade.** Every other component in the panel resolves its colours through `--color-*`
 * (§7.2), so a theme activated in the dashboard repaints it with no rebuild. A paint property
 * handed `var(--color-gap-fill)` is simply an invalid colour — MapLibre rejects it and takes the
 * whole style down with it, and Apex draws the series in black.
 *
 * ⚠️ Moved out of `lib/map/` on 22 August 2026, when the statistics charts became the second
 * consumer. It was never a map concern; it was a theme concern with one caller.
 *
 * So the values are read from the computed style and passed as literals. This is the only mechanism in
 * the panel that reads a colour rather than inheriting it, and it is not licence to write another: the
 * names are theme tokens, typed against the theme document, so a token that does not exist fails to
 * compile rather than falling back to some default nobody chose.
 */
export function useThemeColours<Name extends ColourToken>(
  names: readonly Name[],
): Record<Name, string> {
  // ⚠️ Subscribed to the active theme purely to be re-run when it changes.
  //
  // §7 activates a palette live — the panel is told over MQTT and refetches — and a map whose
  // colours were read once at mount would keep the old palette until the operator reloaded, on the
  // one screen where colour carries meaning rather than decoration.
  const theme = useGetApiV1ThemeActive();
  const version = theme.data?.version;

  // ⚠️ And on daylight/dark, which the version does NOT capture.
  //
  // Switching mode rewrites every `--color-*` on `:root` while the theme document, and therefore
  // its version, stays exactly the same. Without this the map keeps painting the night palette on
  // a daylight page — the one component on the screen that does not follow the cascade, quietly
  // left behind by the switch.
  const { resolved } = useThemeMode();

  // ⚠️ Seeded from the bundled theme, never from an empty string.
  //
  // The first render happens before the effect below has read anything, and MapLibre validates a
  // style the moment it is handed one: an empty value makes it reject the WHOLE style with
  // `color expected, "" found`. Measured in the browser console — and the map still drew its
  // overlays, which is exactly why it would have gone unnoticed.
  const [colours, setColours] = useState<Record<Name, string>>(() => bundled(names));

  useEffect(() => {
    // ⚠️ After paint, never during render. The theme lands on `:root` in App's own effect, and
    // reading the computed style in a render body races that.
    const computed = getComputedStyle(document.documentElement);

    setColours(
      Object.fromEntries(
        names.map((name) => {
          const value = computed.getPropertyValue(`--color-${kebab(name)}`).trim();

          // An unset variable falls back to the bundled theme rather than blanking the layer.
          return [name, value === '' ? DEFAULT_THEME.colors[name] : value];
        }),
      ) as Record<Name, string>,
    );

    // `names` is a literal array at every call site; joining it keeps that from depending on the
    // caller remembering to memoise.
  }, [version, resolved, names.join(',')]);

  return colours;
}

function bundled<Name extends ColourToken>(names: readonly Name[]): Record<Name, string> {
  return Object.fromEntries(names.map((name) => [name, DEFAULT_THEME.colors[name]])) as Record<
    Name,
    string
  >;
}

/** `surfaceElevated` → `surface-elevated`, matching what `applyTheme` writes onto `:root`. */
function kebab(name: string): string {
  return name.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
}
